import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, primeCsrf } from './api';
import type { Paginated } from './paginated';
import type { ClientData, LeadData } from '@/types/api';

const LEADS_KEY = ['leads'] as const;

export type LeadStage = 'new' | 'contacted' | 'qualified' | 'proposal' | 'won' | 'lost';
export type LeadSource = 'web_form' | 'sms' | 'voice' | 'referral' | 'manual';

export const LEAD_STAGES: LeadStage[] = [
  'new',
  'contacted',
  'qualified',
  'proposal',
  'won',
  'lost',
];

export const LEAD_SOURCES: LeadSource[] = [
  'web_form',
  'sms',
  'voice',
  'referral',
  'manual',
];

export type LeadInput = {
  name: string;
  email?: string | null;
  phone?: string | null;
  company_name?: string | null;
  source?: LeadSource;
  stage?: LeadStage;
  owner_user_id?: number | null;
  value_estimate_cents?: number | null;
  notes?: string | null;
  lost_reason?: string | null;
  tags?: string[];
};

export type LeadFilters = {
  q?: string;
  stage?: LeadStage;
  source?: LeadSource;
  page?: number;
};

export function useLeads(filters: LeadFilters = {}) {
  return useQuery({
    queryKey: [...LEADS_KEY, filters],
    queryFn: async (): Promise<Paginated<LeadData>> => {
      const { data } = await api.get<Paginated<LeadData>>('/v1/leads', { params: filters });
      return data;
    },
  });
}

export function useLead(id: number | undefined) {
  return useQuery({
    queryKey: [...LEADS_KEY, id],
    enabled: id != null,
    queryFn: async (): Promise<LeadData> => {
      const { data } = await api.get<LeadData>(`/v1/leads/${id}`);
      return data;
    },
  });
}

export function useCreateLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: LeadInput): Promise<LeadData> => {
      await primeCsrf();
      const { data } = await api.post<LeadData>('/v1/leads', input);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: LEADS_KEY }),
  });
}

export function useUpdateLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      patch,
    }: {
      id: number;
      patch: Partial<LeadInput>;
    }): Promise<LeadData> => {
      await primeCsrf();
      const { data } = await api.patch<LeadData>(`/v1/leads/${id}`, patch);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: LEADS_KEY }),
  });
}

export function useDeleteLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await primeCsrf();
      await api.delete(`/v1/leads/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: LEADS_KEY }),
  });
}

/**
 * Send a client that never closed back to the pipeline. The client is archived
 * and stays linked, so winning the lead later restores it.
 */
export function useMoveClientToLeads() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ clientId, stage }: { clientId: number; stage: LeadStage }): Promise<LeadData> => {
      await primeCsrf();
      const { data } = await api.post<LeadData>(`/v1/clients/${clientId}/move-to-leads`, { stage });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LEADS_KEY });
      qc.invalidateQueries({ queryKey: ['clients'] });
    },
  });
}

export function useConvertLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      leadId,
      payload,
    }: {
      leadId: number;
      payload: Partial<ClientData>;
    }): Promise<ClientData> => {
      await primeCsrf();
      const { data } = await api.post<ClientData>(`/v1/leads/${leadId}/convert`, payload);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LEADS_KEY });
      qc.invalidateQueries({ queryKey: ['clients'] });
    },
  });
}
